<template>
  <div class="container-fluid corrective-view">
    <vm-row class="heading" vm-w="12">
      <vm-col vm-type="flex" vm-align="center" vm-lg="8" vm-sm="12">
        <div>
          <h2>Revisión correctiva</h2>
          <span>Registro de incidencias posteriores a la revisión preventiva.</span>
        </div>
      </vm-col>
      <vm-col vm-type="flex" vm-align="center" vm-justify="flex-end" vm-lg="4" vm-sm="12">
        <vm-button type="border" icon="arrow-back-outline" @click="goBack">
          Volver
        </vm-button>
      </vm-col>
    </vm-row>

    <vm-row v-if="loading" vm-w="12">
      <vm-col class="card" vm-sm="12">
        <p>Por favor espere…</p>
      </vm-col>
    </vm-row>

    <template v-else>
      <vm-row vm-w="12">
        <vm-col vm-lg="7" vm-sm="12">
          <div class="card">
            <h4>Orden</h4>
            <p><b>Folio:</b> {{ orderValue('folio') }}</p>
            <p><b>Razón social:</b> {{ orderValue('social_reason') }}</p>
            <p><b>Estatus surtido:</b> {{ getStatusText(orderValue('status')) }}</p>
          </div>

          <div class="card">
            <h4>Revisión preventiva</h4>
            <template v-if="preventiveReview">
              <p><b>Estado preventivo:</b> {{ preventiveReview.status_label || '—' }}</p>
              <p><b>Resultado:</b> {{ preventiveReview.result_label || '—' }}</p>
              <p><b>Bypass:</b> {{ preventiveReview.bypass_label || '—' }}</p>
              <template v-if="preventiveReview.warehouse_user">
                <p>
                  <b>Revisó almacén:</b>
                  {{ preventiveReview.warehouse_user.name || '—' }}
                  <span v-if="preventiveReview.warehouse_user.submitted_at">
                    — {{ preventiveReview.warehouse_user.submitted_at }}
                  </span>
                </p>
              </template>
              <template v-if="preventiveReview.supervisor_user">
                <p>
                  <b>Confirmó supervisor:</b>
                  {{ preventiveReview.supervisor_user.name || '—' }}
                  <span v-if="preventiveReview.supervisor_user.submitted_at">
                    — {{ preventiveReview.supervisor_user.submitted_at }}
                  </span>
                </p>
              </template>
              <p v-if="preventiveReview.warehouse_comment">
                <b>Comentario almacén:</b> {{ preventiveReview.warehouse_comment }}
              </p>
              <p><b>Comentario preventivo:</b> {{ preventiveReview.comment || '—' }}</p>
            </template>
            <p v-else>Esta orden no tiene revisión preventiva registrada.</p>
          </div>
        </vm-col>

        <vm-col vm-lg="5" vm-sm="12">
          <div v-if="preventiveReview" class="card">
            <h4>Correctivo</h4>
            <vm-input
              type="textarea"
              v-model="form.comment"
              label="Comentarios"
              placeholder="Comentarios del correctivo"
            />
            <div class="resolved-row">
              <span class="resolved-label">Resuelto</span>
              <vm-switch v-model="form.is_resolved" />
            </div>
            <div class="mt-3">
              <vm-button :disabled="!canSubmit" @click="submitCorrective">
                {{ isReadOnly ? 'Actualizar correctivo' : 'Guardar correctivo' }}
              </vm-button>
            </div>
          </div>
        </vm-col>
      </vm-row>

      <vm-row v-if="preventiveReview" vm-w="12">
        <vm-col class="card" vm-sm="12">
          <h4>Detalles con incidencia</h4>
          <vm-table :data="details" max-height="420px">
            <template slot="thead">
              <vm-th>Seleccionar</vm-th>
              <vm-th>Código</vm-th>
              <vm-th>Producto</vm-th>
              <vm-th>Cantidad</vm-th>
              <vm-th>Escaneado</vm-th>
              <vm-th>Error preventivo</vm-th>
              <vm-th>Error correctivo</vm-th>
            </template>
            <template slot-scope="{ data }">
              <vm-tr v-for="(row, index) in data" :key="`detail-${index}`">
                <vm-td>
                  <input
                    type="checkbox"
                    :disabled="isReadOnly"
                    :checked="selectedDetailIds.includes(row.id)"
                    @change="toggleDetail(row.id)"
                  >
                </vm-td>
                <vm-td>{{ row.code }}</vm-td>
                <vm-td>{{ row.name }}</vm-td>
                <vm-td>{{ row.quantity }}</vm-td>
                <vm-td>{{ scannedMap[row.id] !== undefined ? scannedMap[row.id] : '—' }}</vm-td>
                <vm-td>{{ preventiveErrorMap[row.id] || '—' }}</vm-td>
                <vm-td>
                  <template v-if="isDetailInCorrective(row.id)">
                    <vm-select
                      v-model="detailErrors[row.id]"
                      placeholder="Seleccionar error"
                      size="small"
                    >
                      <vm-select-item
                        v-for="item in errorCatalogs"
                        :key="`catalog-${item.id}`"
                        :value="item.id"
                        :text="item.name"
                      />
                    </vm-select>
                  </template>
                  <span v-else>—</span>
                </vm-td>
              </vm-tr>
            </template>
          </vm-table>
        </vm-col>
      </vm-row>

      <vm-row v-if="preventiveReview" vm-w="12">
        <vm-col class="card" vm-sm="12">
          <h4>Logs de escaneo</h4>
          <vm-table v-if="allLogs.length" :data="allLogs" max-height="360px">
            <template slot="thead">
              <vm-th>Tipo</vm-th>
              <vm-th>Código</vm-th>
              <vm-th>Producto</vm-th>
              <vm-th>Cantidad</vm-th>
              <vm-th>Fecha / hora</vm-th>
            </template>
            <template slot-scope="{ data }">
              <vm-tr v-for="(row, index) in data" :key="`log-${index}`">
                <vm-td>{{ row.tipo }}</vm-td>
                <vm-td>{{ row.code || '—' }}</vm-td>
                <vm-td>{{ row.name || '—' }}</vm-td>
                <vm-td>{{ row.quantity }}</vm-td>
                <vm-td>{{ row.occurred_at || '—' }}</vm-td>
              </vm-tr>
            </template>
          </vm-table>
          <p v-else class="mb-0">Sin registros de escaneo.</p>
        </vm-col>
      </vm-row>
    </template>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapActions } = createNamespacedHelpers('OrderCorrectiveReviews')

export default {
  name: 'OrderCorrectiveReview',
  data () {
    return {
      selectedDetailIds: [],
      detailErrors: {},
      form: {
        comment: '',
        is_resolved: false
      },
      status: [
        { text: 'Pendiente', value: 1 },
        { text: 'Parcial', value: 2 },
        { text: 'Completada', value: 3 }
      ]
    }
  },
  computed: {
    ...mapGetters(['loading', 'order', 'preventiveReview', 'correctiveReview', 'details', 'errorCatalogs']),
    isReadOnly () {
      return !!this.correctiveReview
    },
    canSubmit () {
      if (!this.preventiveReview) return false
      if (this.isReadOnly) return true
      return this.selectedDetailIds.length > 0
    },
    scannedMap () {
      if (!this.preventiveReview || !this.preventiveReview.lines) return {}
      return this.preventiveReview.lines.reduce((acc, line) => {
        acc[line.order_detail_id] = line.quantity_scanned
        return acc
      }, {})
    },
    preventiveErrorMap () {
      if (!this.preventiveReview || !this.preventiveReview.lines) return {}
      return this.preventiveReview.lines.reduce((acc, line) => {
        const name = line.error_catalog && line.error_catalog.name
          ? line.error_catalog.name
          : null
        if (line.order_detail_id && name) {
          acc[line.order_detail_id] = name
        }
        return acc
      }, {})
    },
    allLogs () {
      if (!this.preventiveReview) return []
      const warehouse = (this.preventiveReview.warehouse_logs || []).map(log => ({
        ...log,
        tipo: 'Almacén'
      }))
      const supervisor = (this.preventiveReview.supervisor_logs || []).map(log => ({
        ...log,
        tipo: 'Supervisor'
      }))
      return [...warehouse, ...supervisor]
    }
  },
  async created () {
    await this.loadErrorCatalogs()
    const ok = await this.loadOrderCorrectiveContext(this.$route.params.id)
    if (!ok) {
      this.goBack()
      return
    }
    this.hydrateFromExistingCorrective()
  },
  methods: {
    ...mapActions([
      'loadOrderCorrectiveContext',
      'loadErrorCatalogs',
      'createCorrectiveReview',
      'updateCorrectiveReview'
    ]),
    getStatusText (statusCode) {
      const foundStatus = this.status.find(e => e.value === statusCode)
      return foundStatus ? foundStatus.text : '—'
    },
    orderValue (key) {
      if (!this.order) return '—'
      return this.order[key] || '—'
    },
    goBack () {
      this.$router.push('/preventivo-correctivo')
    },
    toggleDetail (detailId) {
      if (this.isReadOnly) return
      if (this.selectedDetailIds.includes(detailId)) {
        this.selectedDetailIds = this.selectedDetailIds.filter(id => id !== detailId)
        const next = { ...this.detailErrors }
        delete next[detailId]
        this.detailErrors = next
      } else {
        this.selectedDetailIds.push(detailId)
        this.$set(this.detailErrors, detailId, null)
      }
    },
    isDetailInCorrective (detailId) {
      return this.selectedDetailIds.includes(detailId)
    },
    buildDetailsPayload () {
      return this.selectedDetailIds.map(id => ({
        id,
        error_catalog_id: this.detailErrors[id] || null
      }))
    },
    hydrateFromExistingCorrective () {
      if (!this.correctiveReview) return
      this.form.comment = this.correctiveReview.comment || ''
      this.form.is_resolved = !!this.correctiveReview.is_resolved
      this.detailErrors = {}
      if (Array.isArray(this.correctiveReview.details)) {
        this.selectedDetailIds = this.correctiveReview.details
          .map(d => d.order_detail_id)
          .filter(Boolean)
        this.correctiveReview.details.forEach(d => {
          if (d.order_detail_id) {
            this.$set(
              this.detailErrors,
              d.order_detail_id,
              d.error_catalog_id || (d.error_catalog && d.error_catalog.id) || null
            )
          }
        })
      }
    },
    async submitCorrective () {
      if (!this.isReadOnly && this.selectedDetailIds.length === 0) {
        this.$store.commit('SET_ALERT_MESSAGE', {
          color: 'warning',
          title: 'Validación',
          text: 'Selecciona al menos un detalle con incidencia.'
        })
        return
      }

      const payload = {
        comment: this.form.comment,
        is_resolved: this.form.is_resolved,
        details: this.buildDetailsPayload()
      }

      const ok = this.isReadOnly
        ? await this.updateCorrectiveReview({
          orderId: this.$route.params.id,
          payload
        })
        : await this.createCorrectiveReview({
          orderId: this.$route.params.id,
          payload
        })

      if (ok) {
        await this.loadOrderCorrectiveContext(this.$route.params.id)
        this.hydrateFromExistingCorrective()
      }
    }
  }
}
</script>

<style lang="scss">
.corrective-view {
  .card {
    margin-bottom: 14px;
    padding: 16px;
  }

  .resolved-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 12px 0;
  }

  .resolved-label {
    font-weight: 600;
  }
}
</style>
